import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock, FileQuestion, AlertTriangle, RefreshCw } from 'lucide-react';
import { Button, EmptyState } from '@ntconsult/aluma-react';
import { cn } from '@common/utils/cn';

type Variant = 'forbidden' | 'notFound' | 'generic';

interface VideoErrorStateProps {
  status?: number;
  isRefetching: boolean;
  onRetry: () => void;
}

export function VideoErrorState({ status, isRefetching, onRetry }: VideoErrorStateProps) {
  const { t } = useTranslation('video');
  const navigate = useNavigate();

  const variant: Variant =
    status === 403 ? 'forbidden' : status === 404 ? 'notFound' : 'generic';

  const Icon = variant === 'forbidden' ? Lock : variant === 'notFound' ? FileQuestion : AlertTriangle;
  const iconClass =
    variant === 'forbidden'
      ? 'text-warning'
      : variant === 'generic'
        ? 'text-danger'
        : 'text-foreground-subtle';

  return (
    <EmptyState
      card
      align="center"
      decoratorPlacement="top"
      decorator={<Icon className={cn('w-8 h-8', iconClass)} aria-hidden="true" />}
      title={t(`errors.${variant}.title`)}
      description={t(`errors.${variant}.message`)}
      action={
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button
            variant="secondary"
            onClick={() => navigate('/files')}
            leftIcon={<ArrowLeft className="w-4 h-4" aria-hidden="true" />}
          >
            {t('errors.backToFiles')}
          </Button>
          {variant === 'generic' && (
            <Button
              onClick={onRetry}
              loading={isRefetching}
              leftIcon={<RefreshCw className="w-4 h-4" aria-hidden="true" />}
            >
              {t('errors.tryAgain')}
            </Button>
          )}
        </div>
      }
    />
  );
}
